import { Box } from "@mui/material";

/**
 * Lisa, as she appears on the onboarding surfaces.
 *
 * Framing ratios read off the approved mock (Figma node 818:7236 on page
 * "Onboarding steps avatar").
 *
 * lisa.png is a 217 x 219 bust on transparency whose opaque silhouette sits at
 * x 14-203, y 5-211 — the canvas carries padding she does not fill. Framing on
 * the canvas rather than on her silhouette wastes roughly 8% of the circle and
 * leaves her hanging off the top of the ring with a gap underneath, so these
 * are deliberate values rather than a plain "fit the image to the box".
 *
 * They are ratios rather than pixels so the welcome dialog can render her
 * larger without the framing drifting.
 */
const IMAGE_WIDTH_RATIO = 0.9645; // 38.58 / 40
const OFFSET_LEFT_RATIO = 0.025; //      1 / 40
const OFFSET_TOP_RATIO = 0.075; //       3 / 40

interface OnboardingAvatarProps {
  /** Diameter in px. The image framing scales with it. */
  size?: number;
}

export default function OnboardingAvatar({ size = 40 }: OnboardingAvatarProps) {
  return (
    <Box
      aria-hidden="true"
      data-testid="onboarding-avatar"
      sx={{
        position: "relative",
        width: size,
        height: size,
        // A long title must not squash her in the header row.
        flexShrink: 0,
        borderRadius: "50%",
        overflow: "hidden",
        // The PNG is transparent, so it needs a ground of its own or she
        // floats on the card.
        backgroundColor: "var(--pi-key-fade)",
        /**
         * An inset shadow, not a border: a border would shrink the content box
         * and shift the image's origin, which breaks the offsets above. Figma
         * strokes sit inside the frame without moving children, and this is the
         * CSS equivalent.
         */
        boxShadow: "inset 0 0 0 1px var(--pi-key-blue)",
      }}
    >
      <img
        src="/lisa.png"
        alt=""
        style={{
          position: "absolute",
          left: size * OFFSET_LEFT_RATIO,
          top: size * OFFSET_TOP_RATIO,
          width: size * IMAGE_WIDTH_RATIO,
          height: "auto",
          display: "block",
        }}
      />
    </Box>
  );
}
